import { createContext, forwardRef, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Sizes } from '@/components/ui/types';

import { TabContentProps, TabProps, TabsProps, TabsVariant } from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'tabs-xs',
  sm: 'tabs-sm',
  md: 'tabs-md',
  lg: 'tabs-lg',
  xl: 'tabs-xl',
};

const variantClasses: Record<TabsVariant, string> = {
  default: '',
  border: 'tabs-border',
  lift: 'tabs-lift',
  box: 'tabs-box',
};

interface TabsContextProps {
  value: string;
  onChange: (value: string) => void;
}
const TabsContext = createContext<TabsContextProps>({
  value: '',
  onChange: () => {},
});
const TabsBase = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      className,
      size = 'md',
      variant = 'default',
      value,
      onChange,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'tabs',
      sizeClasses[size],
      variantClasses[variant],
      className,
    );

    return (
      <TabsContext.Provider value={{ value, onChange }}>
        <div ref={ref} role="tablist" className={classes} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  },
);

const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ className, disabled = false, children, ...props }, ref) => {
    const { value, onChange } = useContext(TabsContext);

    const isActive = value === props.value;

    const classes = twMerge(
      'tab',
      isActive && 'tab-active',
      disabled && 'tab-disabled',
      className,
    );

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        className={classes}
        disabled={disabled}
        aria-selected={isActive}
        onClick={() => onChange?.(props.value as string)}
        {...props}
      >
        {children}
      </button>
    );
  },
);

const Content = forwardRef<HTMLDivElement, TabContentProps>(
  ({ className, children, ...props }, ref) => {
    const { value } = useContext(TabsContext);
    if (value !== props.value) return null;
    return (
      <div
        ref={ref}
        role="tabpanel"
        className={twMerge(
          'tab-content bg-base-100 border-base-300 p-6',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

TabsBase.displayName = 'Tabs';
Tab.displayName = 'Tabs.Tab';
Content.displayName = 'Tabs.Content';

export const Tabs = Object.assign(TabsBase, {
  Tab,
  Content,
});
